"use client";

import type { ReactNode } from "react";
import { usePathname } from "next/navigation";

import { usesAlertsChrome } from "@/lib/alerts/routes";

/**
 * Hides the editorial site chrome on the routes that bring their own
 * (V03 alerts + portal — see `AlertsChrome`).
 *
 * WHY A CLIENT COMPONENT WRAPPING SERVER CHILDREN. The header, footer and
 * margin rail are defined in the ROOT layout, which every route inherits; a
 * nested layout cannot remove them. Reading the current path is the only way
 * to decide, and per
 * `node_modules/next/dist/docs/01-app/03-api-reference/04-functions/use-pathname.md`
 * that is a Client Component hook — "Reading the current URL from a Server
 * Component is not supported."
 *
 * So the predicate is the only thing that ships to the browser: the chrome
 * itself stays a server component and arrives here as `children`. `SITE`,
 * `MainNav` and the copyright year are never pulled into the client bundle.
 *
 * The same doc notes `usePathname` renders into the initial HTML, so the
 * chrome is correctly absent from the server-rendered markup — it is not
 * painted and then removed. Its hydration-mismatch caveat applies only to
 * `rewrites`, and `next.config.ts` defines redirects only.
 *
 * THE ALTERNATIVE, AND WHY NOT. The idiomatic fix is a `(site)` route group
 * holding the editorial chrome, with /alerts and `(portal)` outside it. That
 * is a better shape and it means moving thirteen section directories and
 * every relative import that crosses them — a restructure with real risk, for
 * a layout change. Worth doing on its own ticket; not worth smuggling into
 * this one.
 */
export function SiteChromeSlot({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  if (usesAlertsChrome(pathname)) return null;
  return <>{children}</>;
}
